import {
	getServiceWorkerPath,
	getStoredServiceWorkerVersion,
	hasExpectedServiceWorkerVersion,
	markServiceWorkerVersion,
	SERVICE_WORKER_VERSION
} from "helpers/serviceWorker";
import { FeaturesFlags } from "helpers";
import { useEnvContext } from "contexts";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { onOpenPopUp } from "redux/slices";
import { useAppDispatch } from "./useAppDispatch";
import { usePopupsState } from "./usePopupsState";

export const useServiceWorkerVersionReload = () => {
	const location = useLocation();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { hasSeenEverything } = usePopupsState();
	const dispatch = useAppDispatch();
	const isOutdatedVersionHandlingEnabled = isFeatureFlagEnabled(
		FeaturesFlags.OUTDATED_VERSION_HANDLING
	);
	const [isReloadPending, setIsReloadPending] = useState(false);
	const hadServiceWorkerController = useRef(false);
	const didOpenUpdatePopup = useRef(false);

	useEffect(() => {
		if (!isOutdatedVersionHandlingEnabled || !("serviceWorker" in navigator)) {
			return;
		}

		hadServiceWorkerController.current = Boolean(
			navigator.serviceWorker.controller
		);

		const requestReloadForServiceWorkerVersion = (
			serviceWorker: ServiceWorker | null
		) => {
			if (!hasExpectedServiceWorkerVersion(serviceWorker)) {
				return;
			}

			if (getStoredServiceWorkerVersion() === SERVICE_WORKER_VERSION) {
				return;
			}

			if (!hadServiceWorkerController.current) {
				markServiceWorkerVersion();
				return;
			}

			setIsReloadPending(true);
		};

		const watchServiceWorkerActivation = (
			serviceWorker: ServiceWorker | null
		) => {
			if (!serviceWorker) {
				return;
			}

			serviceWorker.addEventListener("statechange", () => {
				if (serviceWorker.state === "activated") {
					requestReloadForServiceWorkerVersion(serviceWorker);
				}
			});
		};

		const handleControllerChange = () => {
			navigator.serviceWorker
				.getRegistration()
				.then((registration) => {
					requestReloadForServiceWorkerVersion(registration?.active ?? null);
				})
				.catch(() => {
					// Ignore registration lookup failures and keep the current app session.
				});
		};

		const registerServiceWorker = () => {
			navigator.serviceWorker
				.register(getServiceWorkerPath(), { updateViaCache: "none" })
				.then((registration) => {
					requestReloadForServiceWorkerVersion(registration.active);
					watchServiceWorkerActivation(
						registration.installing || registration.waiting
					);

					registration.addEventListener("updatefound", () => {
						watchServiceWorkerActivation(registration.installing);
					});

					return registration.update();
				})
				.then((registration) => {
					requestReloadForServiceWorkerVersion(registration.active);
				})
				.catch((error) => {
					console.error("Service worker registration failed:", error);
				});
		};

		navigator.serviceWorker.addEventListener(
			"controllerchange",
			handleControllerChange
		);

		if (document.readyState === "complete") {
			registerServiceWorker();
		} else {
			window.addEventListener("load", registerServiceWorker, { once: true });
		}

		return () => {
			window.removeEventListener("load", registerServiceWorker);
			navigator.serviceWorker.removeEventListener(
				"controllerchange",
				handleControllerChange
			);
		};
	}, [isOutdatedVersionHandlingEnabled]);

	useEffect(() => {
		if (!isOutdatedVersionHandlingEnabled || !isReloadPending) {
			return;
		}

		if (location.search.length > 1 || !hasSeenEverything) {
			return;
		}

		if (didOpenUpdatePopup.current) {
			return;
		}

		didOpenUpdatePopup.current = true;
		dispatch(onOpenPopUp("app-update-available"));
	}, [
		isOutdatedVersionHandlingEnabled,
		isReloadPending,
		hasSeenEverything,
		location.search,
		dispatch
	]);
};
